import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminShell from "../../Components/admin-shell/AdminShell";
import { fetchHrAccess } from "../../api/HrAPIs";
import { useAuth } from "../../utils/auth";
import { canAccessPanel, isOwner } from "../../utils/roles";

export const STATUS_LABELS = {
  Active: "على رأس العمل",
  Suspended: "موقوف",
  Resigned: "مستقيل",
  Draft: "مسودة",
  Calculated: "تم الحساب",
  PendingApproval: "بانتظار الاعتماد",
  Approved: "معتمد",
  PartiallyPaid: "مدفوع جزئياً",
  Paid: "مدفوع",
  Closed: "مغلق",
  Pending: "قيد المراجعة",
  Rejected: "مرفوض",
  Disbursed: "تم الصرف",
  Submitted: "مرسل",
  Open: "مفتوح",
  Absent: "غياب",
  Monthly: "شهري",
  Daily: "يومي",
  Hourly: "بالساعة",
  Piece: "بالقطعة",
  Custom: "مخصص",
  Fixed: "مبلغ ثابت",
  NextSalary: "من الراتب القادم",
  Installments: "أقساط",
  FixedMonthly: "قسط شهري ثابت",
  Manual: "سداد يدوي",
  Own: "بياناته فقط",
  Department: "القسم",
  Managed: "الموظفون التابعون",
  All: "الجميع",
};

export function labelOf(value) {
  return STATUS_LABELS[value] || value || "—";
}

export function money(value) {
  if (value === null || value === undefined || value === "") return "—";
  return `${Number(value).toLocaleString("en-EG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ج.م`;
}

export function day(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

export function useHr() {
  const { authState } = useAuth();
  const query = useQuery({
    queryKey: ["hrAccess"],
    queryFn: fetchHrAccess,
    enabled: canAccessPanel(authState.role),
    retry: false,
    staleTime: 60_000,
  });
  const owner = isOwner(authState.role);
  const can = (permission) => owner || (query.data?.permissions || []).some((grant) => grant.permission === permission);
  const canAny = (permissions) => owner || permissions.some((permission) => can(permission));
  return { ...query, role: authState.role, owner, can, canAny };
}

export function HrFrame({ title, subtitle, action, children }) {
  return (
    <AdminShell>
      <div className="mx-auto w-full max-w-6xl p-3 pb-16 md:p-6" dir="rtl">
        <ToastContainer position="top-center" />
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900 md:text-2xl">{title}</h1>
            {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
          </div>
          {action}
        </div>
        {children}
      </div>
    </AdminShell>
  );
}

export function HrGuard({ anyOf, children }) {
  const navigate = useNavigate();
  const hr = useHr();
  const allowed = hr.owner || (anyOf ? hr.canAny(anyOf) : (hr.data?.permissions || []).length > 0);
  useEffect(() => {
    if (!hr.isLoading && !hr.isFetching && !allowed) navigate("/dashboard");
  }, [allowed, hr.isFetching, hr.isLoading, navigate]);
  if (!allowed) return null;
  return children;
}

export function StatusPill({ value }) {
  const tone = {
    Active: "bg-emerald-50 text-emerald-700",
    Approved: "bg-emerald-50 text-emerald-700",
    Paid: "bg-emerald-50 text-emerald-700",
    Disbursed: "bg-sky-50 text-sky-700",
    Pending: "bg-amber-50 text-amber-700",
    PendingApproval: "bg-amber-50 text-amber-700",
    Draft: "bg-slate-100 text-slate-600",
    Rejected: "bg-rose-50 text-rose-700",
    Suspended: "bg-rose-50 text-rose-700",
    Resigned: "bg-slate-100 text-slate-600",
    Closed: "bg-slate-100 text-slate-700",
  }[value] || "bg-indigo-50 text-indigo-700";
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{labelOf(value)}</span>;
}

export function Field({ label, children }) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

export const inputClass = "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-indigo-400";

export function Card({ title, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <p className="text-sm text-slate-500">{title}</p>
      <p className="mt-1 text-2xl font-bold text-slate-900">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export function Empty({ text }) {
  return <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500">{text}</p>;
}
